/**
 * Stock take detail page state. The page can open by link (Ollie's "Open
 * stock take") before the venue's stock take list is in Redux, so it must
 * not render dates or the lock state from a missing record.
 *
 * @param {{ stockTake?: object | null, lookupDone: boolean }} options
 * @returns {'ready' | 'loading' | 'not_found'}
 */
export const stockTakeDetailState = ({ stockTake, lookupDone }) => {
  if (stockTake) return 'ready'
  return lookupDone ? 'not_found' : 'loading'
}

export const STOCK_TAKE_NOT_FOUND_COPY = Object.freeze({
  title: 'Stock take not found',
  body: 'It may have been deleted, or it belongs to another venue.',
})

/** Lock line: older locked counts have no `lockedAt`, but are still locked. */
export const stockTakeLockLabel = ({ isLocked, lockTimestamp }) => {
  if (lockTimestamp) return lockTimestamp
  return isLocked ? 'Locked (time not recorded)' : 'Not locked yet'
}

/** Detail HUD: never show lock time until the count is actually locked. */
export const shouldShowStockTakeLockTime = isLocked => Boolean(isLocked)

const trimTakeMeta = value => String(value ?? '').trim()

/**
 * Optional take fields. Empty site/line manager, notes, and placeholders
 * like "No details provided" stay off the HUD.
 */
export const stockTakeVisibleMetaRows = ({
  siteName,
  siteManager,
  lineManager,
  countedByName,
  reasonForCount,
  countNotes,
} = {}) =>
  [
    ['Site name', siteName],
    ['Site manager', siteManager],
    ['Line manager', lineManager],
    ['Counted by', countedByName],
    ['Reason for count', reasonForCount],
    ['Notes', countNotes],
  ]
    .map(([label, value]) => [label, trimTakeMeta(value)])
    .filter(([, value]) => value)

/** Save pill: unsaved vs last persist, or Saved when counts are already on the server. */
export const stockTakeSavePillLabel = ({
  hasUnsavedChanges,
  lastSavedAt,
  hasPersistedCount = false,
} = {}) => {
  if (hasUnsavedChanges) return 'Unsaved counts'
  if (lastSavedAt || hasPersistedCount) return 'Saved'
  return 'No changes yet'
}

export const stockTakeLastSavedLabel = ({
  lastSavedAt,
  hasPersistedCount = false,
  formattedTime = '',
} = {}) => {
  if (lastSavedAt && formattedTime) return formattedTime
  if (lastSavedAt) return String(lastSavedAt)
  if (hasPersistedCount) return 'Saved'
  return 'Not saved yet'
}

/** When the count was created, from creationDate or the id. */
export const stockTakePersistedSavedAt = stockTake => {
  const created = stockTake?.creationDate
    ? new Date(stockTake.creationDate)
    : null
  if (created && !Number.isNaN(created.getTime())) return created
  const id = String(stockTake?._id || '')
  if (!/^[a-f0-9]{24}$/i.test(id)) return null
  const seconds = Number.parseInt(id.slice(0, 8), 16)
  if (!Number.isFinite(seconds)) return null
  const fromId = new Date(seconds * 1000)
  return Number.isNaN(fromId.getTime()) ? null : fromId
}

/** This visit's save wins. Otherwise the count's own saved time. */
export const stockTakeDisplayedSavedAt = (stockTake, sessionSavedAt) => {
  if (sessionSavedAt) {
    const session = new Date(sessionSavedAt)
    if (!Number.isNaN(session.getTime())) return session
  }
  return stockTakePersistedSavedAt(stockTake)
}

/** Who last touched the count. The person who started it is enough. */
export const stockTakeLastEditedBy = stockTake => {
  const events = Array.isArray(stockTake?.lockAuditEvents)
    ? stockTake.lockAuditEvents
    : []
  const latestNamed = [...events]
    .reverse()
    .map(event => String(event?.actorName || '').trim())
    .find(Boolean)
  const name =
    stockTake?.updatedByName ||
    stockTake?.updatedBy?.name ||
    stockTake?.updatedBy?.fullName ||
    stockTake?.updatedBy?.email ||
    stockTake?.lastEditedBy ||
    latestNamed ||
    stockTake?.countedByName ||
    ''
  return String(name || '').trim() || 'Not available'
}
